// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * Merge an INCREMENTAL alert batch into the already-displayed list.
 *
 * The server's /alert-events/alerts-since is strictly incremental — it returns
 * only alerts with `triggeredAt > lastCheck` (simplifiedAlertService.ts), NOT
 * the full current alert state. A polling client must therefore MERGE each
 * batch into what it already shows; assigning the batch directly replaces the
 * whole list and silently drops previously displayed (possibly unacknowledged
 * critical) alerts — the L-020 bug class, three instances fixed 2026-07-20.
 *
 * Incoming alerts come first (server sorts newest-first and everything in the
 * batch is newer than everything already displayed), win over a previous entry
 * with the same id (so a re-fetched alert refreshes acknowledgedAt etc.), and
 * the result is capped to the caller's display limit.
 *
 * One exception to "incoming wins": an acknowledgedAt the client already holds
 * is STICKY. A read can predate an acknowledge that is still in flight (resolve,
 * then Refresh), and letting its unacknowledged copy win put the alert back to
 * unresolved for the rest of the session — nothing re-fetches it, lastCheck is
 * already past it (AlertPoll.tla F2). The server never clears acknowledgedAt;
 * a FAILED acknowledge is rolled back by the caller directly, not via a merge.
 */
export const mergeAlertsById = <T extends { id: number | string; acknowledgedAt?: string | null }>(
  prev: T[],
  incoming: T[],
  cap: number,
): T[] => {
  // Empty batch = nothing new; return prev UNCHANGED (same reference) so the
  // caller's setState is a no-op re-render-wise. prev is already ≤ cap.
  if (incoming.length === 0) return prev
  const prevAck = new Map(prev.filter((a) => a.acknowledgedAt).map((a) => [a.id, a.acknowledgedAt]))
  const refreshed = incoming.map((a) =>
    !a.acknowledgedAt && prevAck.has(a.id) ? { ...a, acknowledgedAt: prevAck.get(a.id) } : a,
  )
  const incomingIds = new Set(incoming.map((a) => a.id))
  return [...refreshed, ...prev.filter((a) => !incomingIds.has(a.id))].slice(0, cap)
}
